import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { supabase } from "./services/supabaseClient";
import "./App.css";
import Login from "./components/Login";
import ResetPassword from "./components/ResetPassword";
import AuthCallback from "./components/AuthCallback";
import OnboardingFlow from "./components/OnboardingFlow";
import History from "./components/History";
import SavedVocab from "./components/SavedVocab";
import Settings from "./components/Settings";
import LibraryRoute from "./routes/LibraryRoute";
import MarketingPage, { MARKETING_ROUTES } from "./components/MarketingPage";
import {
  AuthenticatedAppShell,
  AuthenticatedOutletContext,
} from "./components/AuthenticatedAppShell";
import { UserProfile, UserProvider, useUser } from "./contexts/UserContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import {
  APP_ROUTES,
  clearReturnTarget,
  consumeReturnTarget,
  peekReturnTarget,
  storeReturnTarget,
} from "./routing/appRoutes";

const FullPageLoading: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" />
        <p className="text-gray-600">{t("common.loading")}</p>
      </div>
    </div>
  );
};

interface ProtectedRouteProps {
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  isAuthExit: boolean;
  loginElement: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  isAuthenticated,
  isAuthLoading,
  isAuthExit,
  loginElement,
}) => {
  const location = useLocation();

  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated && !isAuthExit) {
      storeReturnTarget(
        location.pathname + location.search + location.hash
      );
    }
  }, [isAuthenticated, isAuthExit, isAuthLoading, location.hash, location.pathname, location.search]);

  if (isAuthLoading) return <FullPageLoading />;
  if (!isAuthenticated) return loginElement;
  return <Outlet />;
};

type PasswordRecoveryQueryKey = "mode" | "passwordReset";

const passwordRecoveryQueryValues: Record<PasswordRecoveryQueryKey, string> = {
  mode: "forgot-password",
  passwordReset: "success",
};

const CompletedProfileCallbackRedirect: React.FC = () => {
  const [destination] = useState(() => peekReturnTarget());

  useEffect(() => {
    clearReturnTarget();
  }, []);

  return <Navigate to={destination} replace />;
};

const AuthenticatedCallbackRedirect: React.FC<{
  user: UserProfile;
}> = ({ user }) => {
  if (!user.level || !user.interests || user.interests.length === 0) {
    return <Navigate to={APP_ROUTES.onboarding} replace />;
  }

  return <CompletedProfileCallbackRedirect />;
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const testConnection = async () => {
      try {
        console.log("Testing Supabase connection...");
        const { error } = await supabase.from("users").select("count").limit(1);
        if (error) {
          console.log("Supabase connection test:", error.message);
        } else {
          console.log("✅ Supabase connected successfully");
        }
      } catch (err) {
        console.log("❌ Supabase connection failed:", err);
      }
    };

    testConnection();
  }, []);

  const {
    user,
    isAuthenticated,
    isAuthLoading,
    login,
    logout,
    deleteAccount,
    updateUser,
  } = useUser();
  const wasAuthenticated = useRef(false);
  const isAuthExit =
    wasAuthenticated.current && !isAuthLoading && !isAuthenticated;

  useEffect(() => {
    if (isAuthLoading) return;

    if (isAuthExit) {
      clearReturnTarget();
      navigate(APP_ROUTES.root, { replace: true });
    }

    wasAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, isAuthExit, isAuthLoading, navigate]);

  const consumePasswordRecoveryQuery = (
    ...keys: PasswordRecoveryQueryKey[]
  ) => {
    const nextQuery = new URLSearchParams(location.search);
    let changed = false;

    for (const key of keys) {
      if (nextQuery.get(key) === passwordRecoveryQueryValues[key]) {
        nextQuery.delete(key);
        changed = true;
      }
    }

    if (!changed) return;

    const nextSearch = nextQuery.toString();
    const nextUrl =
      location.pathname + (nextSearch ? `?${nextSearch}` : "") + location.hash;
    const historyState = window.history.state;
    window.history.replaceState(historyState, "", nextUrl);
    window.dispatchEvent(new PopStateEvent("popstate", { state: historyState }));
  };

  const acceptUserProfile = (userId: string, userProfile: UserProfile) => {
    login(userId, userProfile);
    consumePasswordRecoveryQuery("mode", "passwordReset");
  };

  const finishInteractiveLogin = (
    userId: string,
    userProfile: UserProfile
  ) => {
    acceptUserProfile(userId, userProfile);
    navigate(consumeReturnTarget(), { replace: true });
  };

  const loginQuery = new URLSearchParams(location.search);
  const requestedLoginMode = loginQuery.get("mode");
  const loginInitialStep =
    requestedLoginMode === "forgot-password"
      ? ("forgot-password" as const)
      : requestedLoginMode === "signup"
        ? ("signup" as const)
        : ("login" as const);
  const passwordResetComplete = loginQuery.get("passwordReset") === "success";
  const hasExplicitPasswordRecoveryExperience =
    loginInitialStep === "forgot-password" || passwordResetComplete;

  const recoveryLogin = (
    <Login
      onLogin={finishInteractiveLogin}
      initialStep={loginInitialStep}
      passwordResetComplete={passwordResetComplete}
      onForgotPasswordExit={() => consumePasswordRecoveryQuery("mode")}
      onContinueToApp={
        isAuthenticated
          ? () => consumePasswordRecoveryQuery("mode", "passwordReset")
          : undefined
      }
    />
  );
  const standardLogin = (
    <Login onLogin={finishInteractiveLogin} initialStep={loginInitialStep} />
  );

  return (
    <Routes>
      <Route path={APP_ROUTES.resetPassword} element={<ResetPassword />} />
      <Route
        path={MARKETING_ROUTES.textSimplifier}
        element={<MarketingPage pageId="text-simplifier" />}
      />
      <Route
        path={MARKETING_ROUTES.epubReader}
        element={<MarketingPage pageId="epub-reader" />}
      />
      <Route
        path={MARKETING_ROUTES.chineseLearners}
        element={<MarketingPage pageId="chinese-learners" />}
      />
      <Route
        path={APP_ROUTES.authCallback}
        element={
          isAuthenticated && user ? (
            <AuthenticatedCallbackRedirect user={user} />
          ) : (
            <AuthCallback onLogin={acceptUserProfile} />
          )
        }
      />
      <Route
        path={APP_ROUTES.onboarding}
        element={
          isAuthLoading ? (
            <FullPageLoading />
          ) : isAuthenticated ? (
            <OnboardingFlow
              userProfile={user}
              onComplete={acceptUserProfile}
            />
          ) : (
            <Navigate to={APP_ROUTES.root} replace />
          )
        }
      />
      <Route
        path={APP_ROUTES.root}
        element={
          hasExplicitPasswordRecoveryExperience ? (
            recoveryLogin
          ) : isAuthLoading ? (
            <FullPageLoading />
          ) : !isAuthenticated ? (
            standardLogin
          ) : (
            <Navigate to={APP_ROUTES.library} replace />
          )
        }
      />
      <Route
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isAuthLoading={isAuthLoading}
            isAuthExit={isAuthExit}
            loginElement={standardLogin}
          />
        }
      >
        <Route
          element={
            user ? (
              <AuthenticatedAppShell
                user={user}
                onLogout={logout}
                onDeleteAccount={deleteAccount}
                onUpdateUser={updateUser}
              />
            ) : (
              <FullPageLoading />
            )
          }
        >
          <Route path={APP_ROUTES.library} element={<LibraryRoute />} />
          <Route path={`${APP_ROUTES.library}/sections/:sectionId`} element={<LibraryRoute />} />
          <Route path={APP_ROUTES.savedVocab} element={<SavedVocabRoute />} />
          <Route path={APP_ROUTES.history} element={<HistoryRoute />} />
          <Route path={APP_ROUTES.settings} element={<SettingsRoute />} />
          <Route path="*" element={<Navigate to={APP_ROUTES.library} replace />} />
        </Route>
      </Route>
    </Routes>
  );
};

const SavedVocabRoute: React.FC = () => {
  const { user } = useOutletContext<AuthenticatedOutletContext>();
  return <SavedVocab userId={user.id} />;
};

const HistoryRoute: React.FC = () => {
  const { user } = useOutletContext<AuthenticatedOutletContext>();
  return <History userId={user.id} />;
};

const SettingsRoute: React.FC = () => {
  const {
    user,
    debugEligible,
    debugModeEnabled,
    onDebugModeChange,
    onUpdateSettings,
    onDeleteAccount,
  } = useOutletContext<AuthenticatedOutletContext>();

  return (
    <Settings
      userId={user.id}
      userName={user.name}
      userEmail={user.email}
      userTargetLevel={user.targetTier}
      debugEligible={debugEligible}
      debugModeEnabled={debugModeEnabled}
      onDebugModeChange={onDebugModeChange}
      onUpdateSettings={onUpdateSettings}
      onDeleteAccount={onDeleteAccount}
    />
  );
};

const App: React.FC = () => (
  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <LanguageProvider>
      <UserProvider>
        <AppContent />
      </UserProvider>
    </LanguageProvider>
  </Router>
);

export default App;
